import React, { Component } from 'react';
import PropTypes from 'prop-types'
import { Router, Route, Redirect, Switch } from 'react-router-dom';
import history from '../../util/history'

import * as firebase from 'firebase';

import FirebaseConst from '../../constants/Firebase';
import App from '../App';
import Login from '../Login';
import DatasetPromise from '../../util/datasetPromise';
import Auth from '../../util/auth';
import initTracker from '../../util/tracker';

import LoaderOverlay from '../../components/LoaderOverlay';
import SignUpPlaceholder from '../SignUpPlaceholder';
import NotAllowed from '../NotAllowed';

/**
 * Delay rendering the `<App/>` until we have reached our verdict about the login status.
 * - Show a loader
 * - Check if the user is logged in
 * - Fetch user data:
 *   - Client
 *   - ACL
 *   - Datasets
 * - Set secured ACL routes
 * - Redirect to sign-up placeholder page if the user isn't linked to any client yet.
 *
 * When logged & authorized this component will:
 * - Render the `App` component
 * - Provide children with context:
 *   - allowed datasets
 *   - list of clients (admin only)
 *   - acl
 */
class AuthLayer extends Component {
  static childContextTypes = {
    datasets: PropTypes.object,
    clients: PropTypes.object,
    acl: PropTypes.object,
  }

  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      loggedIn: undefined,
      authorized: undefined,
      acl: {},
      datasets: {},
    }

    firebase.initializeApp(FirebaseConst.firebaseConfig);
    firebase.auth()
      .onAuthStateChanged(() => {
        return Promise.resolve()
          .then(() => {
            if (!firebase.auth().currentUser) return;

            if (/@(screen6.io|s6.io|youngsons.net)$/.test(firebase.auth().currentUser.email)) {
              firebase.auth().currentUser.isAdmin = true;
              return;
            };

            return firebase.auth().currentUser
              .getIdTokenResult()
              .then(idtRes => {
                firebase.auth().currentUser.isAdmin = !!(idtRes.claims && idtRes.claims.admin);
                return;
              })
          })
          .then(() => {
            return firebase.auth().currentUser;
          })
          .then(this.handleAuthStateChanged);
      });
  }

  /**
   * We are using the context at the moment, because I didn't want to overly complicate our React setup. At some point
   * we'll get rid of the context and will start using stores (redux) to add access to stores instead.
   */
  getChildContext() {
    return {
      datasets: this.state.datasets,
      clients: this.state.clients,
      acl: this.state.acl,
    };
  }

  /**
   * If loggedIn and acl aren't set yet, auth is still in progress
   */
  isAuthInProgress() {
    let { loggedIn, authorized } = this.state;
    return loggedIn === undefined || (loggedIn === true && authorized === undefined);
  }

  showLogin = () => {
    if (this.isAuthInProgress()) {
      return null;
    }
    return this.state.loggedIn ? (
      <Redirect to="/" />
    ) : (
      <Login />
    )
  }

  appOrLogin = (props) => {
    let { acl, loggedIn } = this.state;
    // login / auth still in progress (neither true or false)
    if (this.isAuthInProgress()) {
      return null;
    }

    // Find required acl for this path
    let aclName = Auth.routeToRequiredAcl(props.location.pathname);

    // No access to said ACL item (not authorized)
    if (aclName && !acl[aclName]) {
      return <NotAllowed {...props} />
    }

    return loggedIn ? (
        <App {...props} />
      ) : (
        <Redirect to="/login" />
      )
  }

  handleUserRequest = (client) => {
    // Yes, the user is logged in. Load the datasets belonging to this user.
    this.setState({
      loggedIn: true,
    });

    if (Auth.isAdmin() || client.val()) {
      // Store the client name in the user profile
      firebase.auth().currentUser.s6client = (client && client.val()) || 'screen6';
      if (Auth.isAdmin()) {
        DatasetPromise.getAdminDatasets().then(this.handleDatasetPromise);
      } else {
        DatasetPromise.getUserClientData().then(this.handleDatasetPromise);
      }
    }
    // Otherwise show the new user placeholder page
    else {
      this.setState({ loading: false, authorized: false})
    }
  }

  handleDatasetPromise = ({clients, datasets, acl}) => {
    // Override the defaults with the ACL that was set for this client
    acl = Auth.applyUserAcl(acl);
    this.setState({
      loading: false,
      datasets: datasets,
      clients: clients, // is only populated if admin!
      acl: acl,
      authorized: true,
    });
  }

  handleAuthStateChanged = () => {
    const loginAttempted = !!firebase.auth().currentUser;

    if (process.env.REACT_APP_ANALYTICS_DISABLED !== 'true') { // <- need to check with 'true' because of dotenv
      initTracker(history);
    }

    // If the user has supplied proper credentials first check if it is a known user
    if (loginAttempted) {
      this.setState({
        loading: true,
      });

      // First check if this is an admin
      if (Auth.isAdmin()) {
        this.handleUserRequest();

      // Check if the user is a known user before deemed it a logged-in user
      } else {
        firebase.database().ref(`s6profiles/users/${firebase.auth().currentUser.uid}/client`)
          .once('value')
          .then(this.handleUserRequest);
      }

    // If no proper credentials have been received, or if the user logged out:
    } else {
      this.setState({
        loading: false,
        loggedIn: false,
        authorized: undefined,
      });
    }
  }

  render() {
    return (
      <div>
        <LoaderOverlay visible={this.state.loading}/>
        <Router history={history}>
          <Switch>
            {
              this.state.authorized === false &&
              <SignUpPlaceholder/>
            }
            <Route path="/login" render={this.showLogin} />
            <Route path="*" render={this.appOrLogin} />
          </Switch>
        </Router>
      </div>
    );
  }
}

export default AuthLayer;



// WEBPACK FOOTER //
// ./src/containers/AuthLayer/index.jsx